import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { delay, STAGGER } from '@caios/ui';
import { authClient, gate } from '../auth.ts';

/** After any successful sign-in step: go wherever the gate says. */
export async function afterSignIn(navigate: ReturnType<typeof useNavigate>) {
  const g = await gate();
  if (g.kind === 'enrol' || g.kind === 'recover') return navigate({ to: '/security/setup' });
  if (g.kind === 'verify') return navigate({ to: '/signin/verify' });
  if (g.kind === 'ok') return navigate({ to: '/' });
  return navigate({ to: '/signin' });
}

export function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'password' | 'passkey' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function withPassword(e: FormEvent) {
    e.preventDefault();
    setBusy('password'); setError(null);
    const r = await authClient.signIn.email({ email, password });
    setBusy(null);
    if (r.error) return setError('That email and password don’t match.');
    if ((r.data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) return navigate({ to: '/signin/verify' });
    return afterSignIn(navigate);
  }

  async function withPasskey() {
    setBusy('passkey'); setError(null);
    const r = await authClient.signIn.passkey();
    setBusy(null);
    if (r?.error) return setError('No passkey was used. Try again, or use your password.');
    return afterSignIn(navigate);
  }

  return (
    <div className="cx-app auth-screen">
      <section className="cx-hero-panel auth-card cx-rise">
        <div className="cx-eyebrow cx-rise" style={delay(1, STAGGER.list)}>CAIOS · <b>sign in</b></div>
        <h1 className="cx-display cx-rise" style={delay(2, STAGGER.list)}>Welcome <em>back</em>.</h1>

        <button type="button" className="cx-btn cx-btn--primary auth-wide cx-rise" style={delay(3, STAGGER.list)}
                onClick={withPasskey} disabled={busy !== null} data-testid="signin-passkey">
          {busy === 'passkey' ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
        </button>

        <div className="auth-or cx-label cx-rise" style={delay(4, STAGGER.list)}><span>or with your password</span></div>

        <form onSubmit={withPassword} className="auth-form cx-rise" style={delay(5, STAGGER.list)}>
          <label className="field">
            <span className="cx-label">Email</span>
            <input type="email" autoComplete="username webauthn" required value={email}
                   onChange={(e) => setEmail(e.target.value)} data-testid="signin-email" />
          </label>
          <label className="field">
            <span className="cx-label">Password</span>
            <input type="password" autoComplete="current-password" required value={password}
                   onChange={(e) => setPassword(e.target.value)} data-testid="signin-password" />
          </label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" className="cx-btn auth-wide" disabled={busy !== null} data-testid="signin-submit">
            {busy === 'password' ? 'Signing in…' : 'Continue'}
          </button>
        </form>
      </section>
    </div>
  );
}

export function VerifySecondFactor() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const r = useBackup
      ? await authClient.twoFactor.verifyBackupCode({ code: code.trim() })
      : await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') });
    setBusy(false);
    if (r.error) return setError(useBackup ? 'That backup code isn’t valid or was already used.' : 'That code didn’t work. Check the time on your phone and try the newest code.');
    return afterSignIn(navigate);
  }

  return (
    <div className="cx-app auth-screen">
      <section className="cx-hero-panel auth-card cx-rise">
        <div className="cx-eyebrow">CAIOS · <b>second step</b></div>
        <h1 className="cx-hero">{useBackup ? 'Enter a backup code' : 'Confirm it’s you'}</h1>
        {!useBackup && (<>
          <button type="button" className="cx-btn cx-btn--primary auth-wide" disabled={busy} data-testid="verify-passkey"
                  onClick={async () => { setBusy(true); setError(null); const r = await authClient.signIn.passkey(); setBusy(false); if (r?.error) return setError('No passkey was used. Try again, or enter a code.'); return afterSignIn(navigate); }}>
            Use my passkey
          </button>
          <div className="auth-or cx-label"><span>or the code from your authenticator app</span></div>
        </>)}
        <form onSubmit={submit} className="auth-form">
          <label className="field">
            <span className="cx-label">{useBackup ? 'Backup code' : '6-digit code'}</span>
            <input inputMode={useBackup ? 'text' : 'numeric'} autoComplete="one-time-code" autoFocus required value={code}
                   onChange={(e) => setCode(e.target.value)} className="code-input" data-testid="verify-code" />
          </label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" className="cx-btn cx-btn--primary auth-wide" disabled={busy} data-testid="verify-submit">
            {busy ? 'Checking…' : 'Verify'}
          </button>
        </form>
        <button type="button" className="auth-link" onClick={() => { setUseBackup(!useBackup); setCode(''); setError(null); }}>
          {useBackup ? 'Use my authenticator app instead' : 'Lost your phone? Use a backup code'}
        </button>
      </section>
    </div>
  );
}
